import React from 'react';
import SearchBox from './SearchBox';
import RatingsFilter from './RatingsFilter';
import { HotelFilter } from '@/__generated';
import BasicCheckboxFilter from './BasicCheckboxFilter';
import PriceSliderFilter from './PirceSliderFilter';
import { GetHotelAvailableParams, IHotelQuery } from '@/pages/HotelSearch/type';

interface Props {
  hotelFilter: HotelFilter;
  onFilterChange: (params: Partial<GetHotelAvailableParams>, clearAllFilter?: boolean) => void;
  params: GetHotelAvailableParams | undefined;
  sessionId: string;
  hotelQuery: IHotelQuery | null | undefined;
  onProviderChange: (isAllProvider: boolean) => void;
}

const Sidebar = ({ hotelFilter, onFilterChange, params, sessionId, hotelQuery, onProviderChange }: Props) => {
  const facilitiesFilter = React.useMemo(() => {
    const facilities = hotelFilter.facilities;
    if (facilities) {
      return Object.keys(facilities).map(k => ({
        key: k,
        value: k,
        count: facilities[k],
      }));
    }
    return [];
  }, [hotelFilter]);

  const boardTypesFilter = React.useMemo(() => {
    const boardTypes = hotelFilter.boardTypes;
    if (boardTypes) {
      return Object.keys(boardTypes).map(k => ({
        key: k,
        value: k,
        count: boardTypes[k],
      }));
    }
    return [];
  }, [hotelFilter]);

  const roomsTypeFilter = React.useMemo(() => {
    const roomTypes = hotelFilter.roomTypes;
    if (roomTypes) {
      return Object.keys(roomTypes).map(k => ({
        key: k,
        value: k,
        count: roomTypes[k],
      }));
    }
    return [];
  }, [hotelFilter]);

  const trainFilter = React.useMemo(() => {
    return [
      { key: 'train5Minutes', value: 'Train in 5 minutes', count: hotelFilter.train5Minutes },
      { key: 'train10Minutes', value: 'Train in 10 minutes', count: hotelFilter.train10Minutes },
    ];
  }, [hotelFilter]);

  const areasFilter = React.useMemo(() => {
    return hotelFilter.areas?.map(x => {
      return { value: x.value, label: `${x.value} (${x.count})` };
    });
  }, [hotelFilter]);

  const areaCitiesFilter = React.useMemo(() => {
    if (hotelFilter.areas && params?.areaCode) {
      return hotelFilter.areas.find(x => x.key === params?.areaCode)?.cities || [];
    }
    return [];
  }, [hotelFilter, params]);

  const hotelTypesFilter = React.useMemo(() => {
    const hotelTypes = hotelFilter.hotelTypes;
    if (hotelTypes) {
      return Object.keys(hotelTypes).map(k => ({
        key: k,
        value: k,
        count: hotelTypes[k],
      }));
    }
    return [];
  }, [hotelFilter]);

  const hotelAreaFilter = React.useMemo(() => {
    const areas = hotelFilter.areas;
    if (areas) {
      return areas.map(k => ({
        key: k.key || '',
        value: k.value || '',
        count: k.count,
      }));
    }
    return [];
  }, [hotelFilter]);
  return (
    <>
      {hotelQuery?.allSuppliers ? (
        <div className="sidebar__item hotel-supplier-filter -no-border">
          <h5 className="text-18 fw-500 mb-10">Hotel Providers</h5>
          <div className="sidebar-checkbox">
            <div className="row y-gap-10 items-center justify-between">
              <div className="col-auto">
                <div className="form-checkbox d-flex items-center">
                  <input
                    type="checkbox"
                    onChange={() => {
                      if (params?.allSuppliers) {
                        onProviderChange(false);
                      }
                    }}
                    checked={!params?.allSuppliers}
                  />
                  <div className="form-checkbox__mark rounded-100">
                    <div className="form-checkbox__icon icon-check" />
                  </div>
                  <div className="text-15 ml-10">NTA Hotels Only</div>
                </div>
              </div>
            </div>
            <div className="row y-gap-10 items-center justify-between">
              <div className="col-auto">
                <div className="form-checkbox d-flex items-center">
                  <input
                    type="checkbox"
                    onChange={() => {
                      if (!params?.allSuppliers) {
                        onProviderChange(true);
                      }
                    }}
                    checked={params?.allSuppliers}
                  />
                  <div className="form-checkbox__mark rounded-100">
                    <div className="form-checkbox__icon icon-check" />
                  </div>
                  <div className="text-15 ml-10">All hotels</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {areasFilter?.length ? (
        <BasicCheckboxFilter
          title="Areas"
          selectedValues={params?.areaCode ? [params?.areaCode] : []}
          onChange={(v: any) => {
            onFilterChange({ areaCode: v, cityCodes: [] });
          }}
          isSingleSelect={true}
          data={hotelAreaFilter}
        />
      ) : null}

      {areaCitiesFilter?.length ? (
        <BasicCheckboxFilter
          title="Cities"
          selectedValues={params?.cityCodes}
          data={areaCitiesFilter}
          onChange={values => {
            onFilterChange({ cityCodes: values as any });
          }}
        />
      ) : null}

      {hotelFilter.cities?.length ? (
        <BasicCheckboxFilter
          title="Cities"
          selectedValues={params?.cityCodes}
          data={hotelFilter.cities}
          onChange={values => {
            onFilterChange({ cityCodes: values as any });
          }}
          isSingleSelect={true}
        />
      ) : null}
      {roomsTypeFilter.length ? (
        <BasicCheckboxFilter
          title="Room types"
          selectedValues={params?.roomTypes || []}
          data={roomsTypeFilter}
          onChange={values => {
            onFilterChange({
              roomTypes: (values as any) || undefined,
            });
          }}
        />
      ) : null}

      <RatingsFilter
        selectedValues={params?.starRating || []}
        hotelFilter={hotelFilter}
        onChange={values => {
          onFilterChange({
            starRating: values,
          });
        }}
      />

      {hotelFilter.maxPrice && hotelFilter.minPrice !== hotelFilter.maxPrice ? (
        <div className="sidebar__item">
          <h5 className="text-18 fw-500 mb-10">Price</h5>
          <div className="sidebar-checkbox">
            <PriceSliderFilter
              min={hotelFilter.minPrice || 0}
              max={hotelFilter.maxPrice || 0}
              onChange={(from, to) => {
                onFilterChange({
                  priceFrom: from,
                  priceTo: to,
                });
              }}
            />
          </div>
        </div>
      ) : null}

      {trainFilter.length ? (
        <BasicCheckboxFilter
          title="Access"
          selectedValues={[params?.train5Minutes ? 'train5Minutes' : '', params?.train10Minutes ? 'train10Minutes' : ''].filter(x => x)}
          data={trainFilter}
          isSingleSelect
          onChange={values => {
            onFilterChange({
              train5Minutes: values?.includes('train5Minutes') || undefined,
              train10Minutes: values?.includes('train10Minutes') || undefined,
            });
          }}
        />
      ) : null}

      {hotelTypesFilter.length ? (
        <BasicCheckboxFilter
          title="Hotel types"
          selectedValues={params?.hotelTypes || []}
          data={hotelTypesFilter}
          onChange={values => {
            onFilterChange({ hotelTypes: values as any });
          }}
        />
      ) : null}

      {/* {!params?.allSuppliers && boardTypesFilter.length ? (
        <BasicCheckboxFilter
          title="Board types"
          selectedValues={params?.boardTypes || []}
          data={boardTypesFilter}
          onChange={values => {
            onFilterChange({
              boardTypes: (values as any) || undefined,
            });
          }}
        />
      ) : null} */}

      <div className="sidebar__item ">
        <h5 className="text-18 fw-500 mb-10 d-flex justify-content-between">
          Search by property name
          {params?.hotelName ? (
            <button onClick={() => onFilterChange({ hotelName: '' })}>
              <i className="icon-close text-14 text-red-1" />
            </button>
          ) : null}
        </h5>
        <SearchBox params={params} sessionId={sessionId} onChange={v => onFilterChange({ hotelName: v })} />
      </div>

      {facilitiesFilter.length ? (
        <BasicCheckboxFilter
          title="Facilities"
          selectedValues={params?.facilities || []}
          data={facilitiesFilter}
          onChange={values => {
            onFilterChange({
              facilities: (values as any) || undefined,
            });
          }}
        />
      ) : null}
    </>
  );
};

export default Sidebar;
